import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Radio, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useImportScript } from '@/api/mutations/useProjectMutations';
import { IMPORT_MODAL_DEFAULTS, importModalAtom } from '@/atoms/import';
import { CheckboxField } from '@/components/CheckboxField/CheckboxField';
import { FormTip } from '@/components/FormTip/FormTip';
import { Button } from '@/components/ui/button';
import { episodeImport, episodeImportLoader, snackbarColor } from '@/enums';
import { useRequiredAppSelector, useSnackbar } from '@/hooks';
import { isApiError } from '@/types';
import {
    ImportCheckboxGroup,
    ImportEpisodeGroup,
    ImportForm,
    ImportFormActions,
    ImportFormWrap,
    ImportInfo,
    ImportRadio,
} from './ImportScript.styles';
import { ImportScriptEpisode } from './ImportScriptEpisode';
import { ImportScriptInfo } from './ImportScriptInfo';
import { ImportScriptRulesButton } from './ImportScriptRulesButton';
import { ImportScriptUpload } from './ImportScriptUpload';

export type TImportValues = {
    episode: episodeImport;
    episodeId: number;
    logline: boolean;
    act: boolean;
    characters: boolean;
    file: File | null;
};

const initialValues: TImportValues = {
    episode: episodeImport.new,
    episodeId: 0,
    logline: true,
    act: false,
    characters: false,
    file: null,
};

export const ImportScriptForm = () => {
    const { t } = useTranslation();
    const [importModal, setImportModal] = useAtom(importModalAtom);
    const showSnackbar = useSnackbar();
    const project_id = useRequiredAppSelector('projectId');
    const { checked, ready, showCharacters, characters_union } = importModal;
    const { mutateAsync: importScriptAsync } = useImportScript();

    const formik = useFormik({
        initialValues,
        onSubmit: async values => {
            const data = new FormData();
            values.logline && data.append('logline', String(+values.logline));
            values.act && data.append('act', String(+values.act));
            values.characters && data.append('characters', String(+values.characters));
            values.file && data.append('file', values.file);
            characters_union && data.append('characters_map', JSON.stringify(characters_union));

            !ready && !characters_union && data.append('preview', '1');

            if (values.episode === episodeImport.current) {
                data.append('episode_id', String(values.episodeId));
            }

            setImportModal(prev => ({
                ...prev,
                showCharacters: false,
                showStatistics: showCharacters,
                loader: ready ? episodeImportLoader.importing : episodeImportLoader.checking,
            }));
            try {
                const importResult = await importScriptAsync({ project_id, data });
                setImportModal(prev => ({
                    ...prev,
                    loader: ready ? episodeImportLoader.done : false,
                    showRules: false,
                    showStatistics: true,
                    checked: true,
                    ready: !importResult.exceptions.length,
                    statistics: importResult,
                    characters_union: null,
                }));
            } catch (error) {
                setImportModal(prev => ({ ...prev, loader: false }));
                if (isApiError(error)) {
                    showSnackbar({
                        open: true,
                        message: error.data.message,
                        severity: snackbarColor.error,
                    });
                }
            }
        },
    });

    const handleCancel = () => {
        setImportModal(() => IMPORT_MODAL_DEFAULTS);
    };

    return (
        <ImportFormWrap>
            <ImportForm onSubmit={formik.handleSubmit}>
                <Typography
                    variant="subtitle4"
                    display="block"
                    mb="16px"
                    children={t('import.import-script')}
                    sx={theme => ({ color: theme.custom.c1 })}
                />

                {!checked && (
                    <>
                        <ImportEpisodeGroup value={formik.values.episode} name="episode" onChange={formik.handleChange}>
                            <ImportScriptEpisode formik={formik} />
                            <ImportRadio control={<Radio />} value={episodeImport.new} label={t('import.options.episode-new')} />
                        </ImportEpisodeGroup>
                        <ImportCheckboxGroup>
                            <CheckboxField
                                name="logline"
                                label={t('import.options.loglines')}
                                checked={formik.values.logline}
                                onChange={formik.handleChange}
                            />
                            <CheckboxField
                                name="act"
                                label={t('import.options.acts')}
                                checked={formik.values.act}
                                onChange={formik.handleChange}
                            />
                            <CheckboxField name="renumber" label={t('import.options.renumber')} disabled />
                            <CheckboxField name="scene-day" label={t('import.options.scene-day')} disabled />
                            <CheckboxField
                                name="characters"
                                label={t('import.options.characters-by-dialogue')}
                                checked={formik.values.characters}
                                onChange={formik.handleChange}
                            />
                        </ImportCheckboxGroup>
                    </>
                )}

                <ImportScriptUpload formik={formik} />

                {!checked && (
                    <ImportInfo>
                        <FormTip icon={<ErrorOutlineIcon />} children={t('import.check-info')} />
                        <ImportScriptRulesButton />
                    </ImportInfo>
                )}

                <ImportFormActions>
                    {checked ? (
                        <Button variant="contained" type="submit" children={ready ? t('buttons.import') : t('buttons.check-repeat')} />
                    ) : (
                        <Button variant="contained" type="submit" children={t('buttons.check')} disabled={!formik.values.file} />
                    )}
                    <Button variant="outlined" onClick={handleCancel} children={checked ? t('close') : t('buttons.cancel')} />
                </ImportFormActions>
            </ImportForm>
            <ImportScriptInfo />
        </ImportFormWrap>
    );
};
